/**
 * `initialView` — resolve the View the SPA mounts on first render from the
 * current URL. Extracted from `App.tsx` (2026-07-07) so tests exercise the
 * REAL function rather than a hand-copied mirror (QA finding on the
 * connect-return-URL fix: two independent copies do not cross-check).
 */

import { sanitizePrefillDomainParam } from '../lib/prefill-domain.js';
import type { View } from './view-router.js';

/**
 * Views that may be restored from a `?view=` URL param on load (written by
 * `setView` via `replaceState`). Allow-list ONLY — auth-flow views
 * (`login` / `verify`) and transient ones are never restored from a URL, so
 * an untrusted `?view=` can at worst land on an in-app tab the merchant could
 * already reach.
 */
export const RESTORABLE_VIEWS = [
  'sites', 'onboarding', 'mandates', 'settings', 'billing', 'account',
  'dashboard', 'resolution', 'guardrails', 'catalog', 'visibility', 'readiness',
  'simulation', 'benchmarks', 'agents', 'security',
  // Agency Portal v2 Slice 4 — one restorable view for the whole portal.
  'agencyPortal',
  // Legacy aliases kept so pre-portal deep links keep working. The view
  // router redirects each into `agencyPortal` with the matching
  // `?section=` so the dashboard lands on the right panel.
  // ADR-0237 Slice 1
  'clients',
  // ADR-0240 Slice 2a
  'agencyReferral',
  // ADR-0240 Slice 2b
  'agencyReports',
  // ADR-0240 Slice 2c
  'whiteLabel',
  // ADR-0240 Slice 2d
  'agencyWidget',
] as const;

/**
 * Rewrite the current URL to Security's sub-tab, strip the legacy
 * sub-tab params that pointed at the old location, and preserve the
 * `?threat=` deep link so threat alert emails keep landing on their
 * exact sender. Returns `'security'` so the caller completes routing.
 */
function rewriteToSecurity(
  url: URL,
  section: 'summary' | 'attackers' | 'paths' | 'blocking' | 'trusted',
  legacyParams: readonly string[],
): View {
  try {
    url.searchParams.set('view', 'security');
    url.searchParams.set('section', section);
    for (const p of legacyParams) url.searchParams.delete(p);
    window.history.replaceState(null, '', url.toString());
  } catch {
    /* best effort — URL rewrite is enhancement only */
  }
  return 'security';
}

export function initialView(): View {
  if (typeof window === 'undefined') return 'sites';
  const url = new URL(window.location.href);
  // ADR-0185 — the new-account confirm interstitial. MUST resolve before
  // the `?token=` check below: the /welcome URL carries the magic token
  // too, and falling through to `verify` would bounce the token straight
  // back to the API instead of rendering the confirm page.
  if (url.pathname.startsWith('/welcome')) {
    return 'welcome';
  }
  // ADR-0216 — support-login redeem landing. Match BEFORE the /verify
  // fallback so `/support-login#token=…` never routes through the
  // magic-link verify bridge. The token is in the fragment (never the
  // query), so the `?token=` check below does not confuse it.
  if (url.pathname.startsWith('/support-login')) {
    return 'supportLogin';
  }
  // ADR-0237 — agency-access approval landing. Match BEFORE the
  // `?token=` fallback so a link-request email never routes through
  // the magic-link verify bridge (the token here is an approval
  // token, not a sign-in token).
  if (url.pathname.startsWith('/settings/agency-access')) {
    return 'agencyAccessApproval';
  }
  // ADR-0261 — assistant MCP OAuth consent landing. The server mints
  // `oar_<ULID>` request ids and redirects the client to
  // `/connect/<request_id>`. Resolve BEFORE the `?token=` fallback so
  // the magic-link verify bridge never swallows it, and BEFORE the
  // `/sign-in` branch so a signed-out visitor still lands on the
  // consent view (where Login renders in place with the return path
  // saved).
  if (url.pathname.startsWith('/connect/')) {
    return 'connectApproval';
  }
  if (url.pathname.startsWith('/verify') || url.searchParams.has('token')) {
    return 'verify';
  }
  // ADR-0201 follow-up (qa #570 major 1) — the report page's claim CTA
  // links /sign-in?prefill_domain={domain}. Signed OUT, the 401
  // bootstrap mounts Login regardless of this view and Login rides the
  // param onto the sign-in POST. Signed IN, the bootstrap succeeds and
  // THIS mapping is what keeps the promise: route to onboarding, where
  // `consumePrefillDomain` (path-agnostic — it reads the search params)
  // picks the domain up and the existing pending-domain resume takes
  // over. A bare /sign-in without a valid domain falls through to
  // 'sites' (pre-existing behavior).
  // Pathname lowercased to match `isClaimEntry` exactly — the two
  // guards must not drift (security review on #570, minor 1).
  const path = url.pathname.toLowerCase();
  if (
    (path === '/sign-in' || path.startsWith('/sign-in/'))
    && sanitizePrefillDomainParam(url.searchParams.get('prefill_domain')) !== null
  ) {
    return 'onboarding';
  }
  // URL-persisted view (founder report 2026-06-12: refresh lost the active
  // tab; email deep links landed on Sites). `setView` writes `?view=` via
  // replaceState; restore it here.
  //
  // Precedence over the `/onboarding` pathname (2026-07-07): once a merchant
  // has consumed the OAuth-return signal (CompleteStep strips `?connected=` on
  // mount — see `CompleteStep.tsx`), a subsequent refresh on
  // `/onboarding?view=<X>` is a NAVIGATION intent, not a fresh OAuth return.
  // Respect the intended view instead of stranding the merchant back on the
  // onboarding shell. `?connected=` presence is the fresh-return marker: while
  // it's set, onboarding wins (the OAuth-return path REQUIRES the onboarding
  // shell to mount so `useShopifyOauthReturn` can poll + advance). The `?view=`
  // param is written by `setView`, so an untrusted value can only arise if the
  // merchant navigated TO that view inside the app first.
  const requested = url.searchParams.get('view');
  const hasOauthReturn = url.searchParams.get('connected') !== null;
  if (
    !hasOauthReturn
    && requested !== null
    && (RESTORABLE_VIEWS as readonly string[]).includes(requested)
  ) {
    // `?view=visibility` is the legacy nav id — resolve it to the Agent
    // Readiness view that replaced the Visibility page in-place.
    if (requested === 'visibility') return 'readiness';
    // Founder 2026-10-20 — Security moved three merchant surfaces
    // under one tab:
    //   - `?view=mandates&segment=suspicious` → Security · Attackers
    //     (threat alert emails + Slack digests still link here)
    //   - `?view=settings&settings_section=blocking` + the two
    //     blocking tabs → Security · Blocking | Attack paths
    //   - `?view=agents&agents_mode=probes` → Security · Attack paths
    // Each redirect rewrites the URL in place so Back / refresh land
    // where the merchant expected, and so the destination view reads
    // its sub-tab from `?section=`.
    if (requested === 'mandates' && url.searchParams.get('segment') === 'suspicious') {
      return rewriteToSecurity(url, 'attackers', ['segment']);
    }
    if (requested === 'settings') {
      const section = url.searchParams.get('settings_section');
      if (section === 'blocking' || section === 'install_blocking') {
        const paths = url.searchParams.get('blocking_tab') === 'paths';
        return rewriteToSecurity(url, paths ? 'paths' : 'blocking', [
          'settings_section', 'settings_tab', 'blocking_tab',
        ]);
      }
    }
    if (requested === 'agents' && url.searchParams.get('agents_mode') === 'probes') {
      return rewriteToSecurity(url, 'paths', ['agents_mode']);
    }
    return requested as View;
  }
  // Deep links Settings used to own and that the Agent Readiness page now
  // absorbs. `settings_tab=scan` → readiness; `settings_tab=llms_txt` →
  // readiness with the llms.txt editor drawer open. The destination view
  // consumes the one-shot param on mount.
  const settings_tab = url.searchParams.get('settings_tab');
  if (settings_tab === 'scan' || settings_tab === 'llms_txt') {
    return 'readiness';
  }
  // ADR-0053 §1.1 — the Shopify OAuth callback redirects merchants to
  // `/onboarding?connected=shopify&site=<id>`. Recognize the pathname so the
  // SPA mounts the Onboarding shell on initial render rather than defaulting
  // to the sites view. `?connected=shopify` is then picked up by
  // `useShopifyOauthReturn` + the InstallVerifyStep advance effect; `?site=`
  // is consumed in the SiteProvider bootstrap.
  if (url.pathname.startsWith('/onboarding')) {
    return 'onboarding';
  }
  // ADR-0107 Slice 3 — the Stripe OAuth callback redirects to
  // `/?settings_tab=integrations&connected=stripe&site=<id>`. Route to
  // 'settings' so the merchant lands directly on Settings → Integrations
  // with their newly-connected account visible. `?site=` and
  // `?settings_tab=` are consumed by SiteProvider and useSettingsDeepLink
  // respectively on mount.
  if (hasOauthReturn && url.searchParams.get('connected') === 'stripe') {
    return 'settings';
  }
  // The Stripe OAuth callback's role re-check deny (the member who began
  // the install was demoted / suspended / deactivated inside the state TTL)
  // redirects to `/?settings_tab=integrations&connect_error=role_insufficient`.
  // Route to Settings so SettingsIntegrations mounts and surfaces the toast.
  if (
    url.searchParams.get('connect_error') !== null
    && settings_tab === 'integrations'
  ) {
    return 'settings';
  }
  return 'sites';
}
